import { queryOptions } from '@tanstack/react-query';
import * as dietaryProfilesApi from '../endpoints/dietary-profile';
import { ListDietaryProfilesParams } from '../endpoints/dietary-profile';
import { DietaryProfile } from '@/types/dietary-profile';
import { Paginated } from '@/types/pagination';

/**
 * Query keys for dietary profiles.
 */
export const DIETARY_PROFILES_QUERY_KEYS = {
  all: ['dietary-profiles'] as const,
  lists: () => [...DIETARY_PROFILES_QUERY_KEYS.all, 'list'] as const,
  list: (params: ListDietaryProfilesParams) =>
    [...DIETARY_PROFILES_QUERY_KEYS.lists(), params] as const,
  details: () => [...DIETARY_PROFILES_QUERY_KEYS.all, 'detail'] as const,
  detail: (slug: string) => [...DIETARY_PROFILES_QUERY_KEYS.details(), slug] as const,
} as const;

/**
 * Query options for listing dietary profiles.
 *
 * Reference data changes rarely, so it is cached with a long staleTime and
 * persisted to disk so consumers paint instantly on cold boot.
 */
export const dietaryProfileListQueryOptions = (params: ListDietaryProfilesParams = {}) =>
  queryOptions({
    queryKey: DIETARY_PROFILES_QUERY_KEYS.list(params),
    queryFn: async (): Promise<Paginated<DietaryProfile>> => {
      return dietaryProfilesApi.listDietaryProfiles(params);
    },
    staleTime: 1000 * 60 * 15, // 15 minutes (near-static reference data)
    gcTime: 1000 * 60 * 30, // 30 minutes
    meta: {
      // Near-static reference data; persist so it paints instantly.
      persist: true,
    },
  });

/**
 * Query options for fetching a single dietary profile by slug.
 */
export const dietaryProfileDetailQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: DIETARY_PROFILES_QUERY_KEYS.detail(slug),
    queryFn: async (): Promise<DietaryProfile> => {
      return dietaryProfilesApi.getDietaryProfile(slug);
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 15, // 15 minutes (near-static reference data)
    gcTime: 1000 * 60 * 30, // 30 minutes
    meta: {
      persist: true,
    },
  });
